
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiX, FiUploadCloud, FiCheck, FiAlertTriangle, FiPlus, FiArrowRight, FiSettings, FiAlertCircle, FiFilter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { parseOfx } from '../../utils/ofxParser';
import Api from '../../Api';
import Button from '../Button';
import TransactionForm from '../TransactionForm';
import Modal from '../Modal';
import Tooltip from '../Tooltip';
import Select from '../Select';
import './style.css';

export default function OfxImportModal({ isOpen, onClose, onImport, account, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, review
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configuringTransaction, setConfiguringTransaction] = useState(null); // index of transaction being configured
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [ofxBalance, setOfxBalance] = useState(null);
  const [updateBalance, setUpdateBalance] = useState(false);
  const fileInputRef = useRef(null);
  
  const [targetCompanyId, setTargetCompanyId] = useState('');

  useEffect(() => {
    if (isOpen) {
        setTargetCompanyId('');
        setStep('upload');
        setTransactions([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && targetCompanyId && targetCompanyId !== 'all') {
        Promise.all([
            Api.getCategories(targetCompanyId),
            Api.getCostCenters(targetCompanyId)
        ]).then(([cats, ccs]) => {
            setCategories(cats);
            setCostCenters(ccs);
        }).catch(err => console.error("Error loading aux data", err));
    } else {
        setCategories([]);
        setCostCenters([]);
    }
  }, [isOpen, targetCompanyId]);

  const companyOptions = useMemo(() => {
    if (!companies || !Array.isArray(companies)) return [];
    
    // Sempre tentamos listar todas as empresas vinculadas à conta
    let availableCompanies = companies;
    
    // Se a conta tiver a lista de empresas vinculadas, filtramos por ela
    if (account && account.companies && Array.isArray(account.companies) && account.companies.length > 0) {
        const linkedIds = account.companies.map(c => String(c.id));
        availableCompanies = companies.filter(c => linkedIds.includes(String(c.id)));
    } else if (account && account.company_id) {
         // Fallback para conta antiga com apenas uma empresa
         availableCompanies = companies.filter(c => String(c.id) === String(account.company_id));
    }

    return availableCompanies.map(c => ({
        value: String(c.id),
        label: c.trade_name || c.corporate_name || c.name || `Empresa ${c.id}`,
    }));
  }, [companies, companyId, account]);

  if (!isOpen) return null;

  const handleQuickUpdate = (index, field, value) => {
    const newTransactions = [...transactions];
    const t = newTransactions[index];
    t[field] = value;
    
    // Check if fully configured (Simplified check for quick actions)
    // We assume if user sets Category and Cost Center, and has a Partner, it's ready.
    const hasCategory = !!t.categoryId;
    const hasCostCenter = !!t.costCenterId; 
    const hasRelation = !!t.relationId || !!t.autoCreateEntity;
    
    if (hasCategory && hasCostCenter && hasRelation) {
        t.configured = true;
    } else if (!hasCategory || !hasCostCenter) {
        // Only unconfigure if missing mandatory fields
        t.configured = false;
    }
    
    setTransactions(newTransactions);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!targetCompanyId) {
        toast.error('Selecione uma empresa para continuar.');
        return;
    }

    try {
      setLoading(true);
      const text = await file.text();
      const { transactions: parsed, balance } = parseOfx(text);
      
      if (parsed.length === 0) {
        toast.warning('Nenhuma transação encontrada no arquivo.');
        setLoading(false);
        return;
      }
      
      setOfxBalance(balance);
      if (balance) {
          setUpdateBalance(false); 
      }

      // Check for reconciliation matches
      const effectiveCompanyId = targetCompanyId;
      const response = await Api.reconcilePreview(effectiveCompanyId, account.id, parsed);
      
      // Auto-configure pending reconciliations
      const mappedTransactions = response.map(t => {
          const isPending = t.reconciliation_status === 'CONCILIADA_PENDENTE';
          return {
              ...t,
              // Mark as configured if it's a pending reconciliation (we just need to confirm)
              configured: t.configured || isPending,
              // Optional: Auto-select pending reconciliations
              // selected: isPending 
          };
      });

      setTransactions(mappedTransactions);
      setStep('review');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao ler arquivo OFX.');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Create a synthetic event
      handleFileChange({ target: { files: [file] } });
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const selected = transactions.filter(t => t.selected);
      
      if (selected.length === 0) {
        toast.warning('Selecione pelo menos uma transação para importar.');
        return;
      }

      // Validate configurations
      const unconfigured = selected.filter(t => {
          // If it's a new item OR a forced new item
          const isNew = t.match_type === 'none' || t.forceNew;
          // It must be configured
          return isNew && !t.configured;
      });

      if (unconfigured.length > 0) {
        toast.error(`Existem ${unconfigured.length} lançamento(s) selecionado(s) que precisam ser configurados antes da importação.`);
        setLoading(false); // Ensure loading state is reset
        return;
      }

      if (!targetCompanyId) {
          toast.error('Empresa não selecionada.');
          setLoading(false);
          return;
      }

      const effectiveCompanyId = targetCompanyId;

      await Api.importOfxTransactions(effectiveCompanyId, account.id, selected, updateBalance, ofxBalance?.amount);
      toast.success(`${selected.length} transações importadas com sucesso!`);
      if (updateBalance) {
        toast.success('Saldo da conta atualizado conforme o extrato OFX.');
      }
      onImport();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar transações.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = (index, e) => {
    e.stopPropagation();
    setConfiguringTransaction(index);
  };

  const handleSaveConfiguration = async (data) => {
    // Handle FormData if present (attachments not supported in this view yet, stripping them)
    let payload = data;
    if (data instanceof FormData) {
        payload = {};
        data.forEach((value, key) => {
            if (key === 'files') return;
            try {
                const parsed = JSON.parse(value);
                payload[key] = parsed;
            } catch (e) {
                payload[key] = value;
            }
        });
    }

    const newTransactions = [...transactions];
    const index = configuringTransaction;
    const original = newTransactions[index];
    
    // Merge configured data
    newTransactions[index] = {
        ...original,
        ...payload,
        configured: true,
        // Update display fields
        amount: payload.type === 'expense' ? -Math.abs(payload.value) : Math.abs(payload.value),
        memo: payload.description || original.memo,
        date: payload.competenceDate ? payload.competenceDate.split('/').reverse().join('-') : original.date,
    };
    
    setTransactions(newTransactions);
    setConfiguringTransaction(null);
    toast.success('Lançamento configurado!');
  };

  const handleApplyToSelected = async (data) => {
    let payload = data;
    if (data instanceof FormData) {
        payload = {};
        data.forEach((value, key) => {
            if (key === 'files') return;
            try {
                const parsed = JSON.parse(value);
                payload[key] = parsed;
            } catch (e) {
                payload[key] = value;
            }
        });
    }

    const newTransactions = [...transactions];
    let count = 0;

    newTransactions.forEach((t, index) => {
        if (index === configuringTransaction || t.selected) {
            const originalAmount = Math.abs(t.amount);
            const newType = payload.type;
            
            // Recalculate rateio values based on percentage and target amount
            let newRateio = payload.rateio;
            if (Array.isArray(payload.rateio)) {
                newRateio = payload.rateio.map(item => ({
                    ...item,
                    value: (item.percentage / 100) * originalAmount
                }));
            }

            newTransactions[index] = {
                ...t,
                ...payload,
                rateio: newRateio,
                
                // Restore unique fields
                date: t.date, 
                memo: index === configuringTransaction ? (payload.description || t.memo) : t.memo,
                amount: newType === 'expense' ? -originalAmount : originalAmount,
                configured: true,
                
                // Ensure specific fields are not overwritten by payload if they shouldn't be
                value: originalAmount, 
            };
            count++;
        }
    });

    setTransactions(newTransactions);
    setConfiguringTransaction(null);
    toast.success(`Configuração aplicada a ${count} lançamentos!`);
  };

  const handleClose = () => {
    setStep('upload');
    setTransactions([]);
    onClose();
  };

  const toggleSelection = (index) => {
    const newTransactions = [...transactions];
    newTransactions[index].selected = !newTransactions[index].selected;
    setTransactions(newTransactions);
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Importar Extrato OFX - ${account?.name}`}
      size="xl"
    >
      <div className="ofx-import-body">
        <div className="ofx-content-area">
          {step === 'upload' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '20px' }}>
              <div style={{ width: '100%'}}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Selecione a Empresa *
                </label>
                <Select
                  options={companyOptions}
                  value={targetCompanyId}
                  onChange={(e) => setTargetCompanyId(e.target.value)}
                  placeholder="Selecione uma empresa..."
                  searchable
                />
                {!targetCompanyId && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiAlertTriangle size={14} />
                    <span>Selecione uma empresa para continuar</span>
                  </div>
                )}
              </div>

              <div 
                className="ofx-upload-area"
                onClick={() => {
                  if (!targetCompanyId) {
                    toast.warning('Por favor, selecione a empresa antes de enviar o arquivo.');
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  if (!targetCompanyId) {
                    e.preventDefault();
                    toast.warning('Por favor, selecione a empresa antes de enviar o arquivo.');
                    return;
                  }
                  handleDrop(e);
                }}
                style={{ 
                  opacity: (!targetCompanyId) ? 0.5 : 1,
                  cursor: (!targetCompanyId) ? 'not-allowed' : 'pointer',
                  backgroundColor: (!targetCompanyId) ? '#f3f4f6' : 'transparent',
                  borderColor: (!targetCompanyId) ? '#d1d5db' : 'var(--primary-color)'
                }}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  accept=".ofx"
                  onChange={handleFileChange}
                  disabled={!targetCompanyId}
                />
                <FiUploadCloud className="ofx-upload-icon" style={{ color: !targetCompanyId ? '#9ca3af' : 'var(--primary-color)' }} />
                <div className="ofx-upload-text" style={{ color: !targetCompanyId ? '#6b7280' : 'inherit' }}>
                    {!targetCompanyId ? "Selecione uma empresa acima para liberar o upload" : "Clique para selecionar ou arraste o arquivo aqui"}
                </div>
                <div className="ofx-upload-subtext" style={{ visibility: !targetCompanyId ? 'hidden' : 'visible' }}>Arquivos .OFX ou .OFC</div>
              </div>
            </div>
          ) : (
            <div className="ofx-table-container">
              <table className="ofx-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input 
                        type="checkbox" 
                        checked={Array.isArray(transactions) && transactions.length > 0 && transactions.every(t => t.selected)}
                        onChange={(e) => {
                          if (!Array.isArray(transactions)) return;
                          const checked = e.target.checked;
                          setTransactions(transactions.map(t => {
                            if (t.reconciliation_status === 'CONCILIADA_TOTALMENTE' && !t.forceNew) return t;
                            return { ...t, selected: checked };
                          }));
                        }}
                      />
                    </th>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th style={{ width: '100px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(transactions) && transactions.map((t, index) => (
                    <tr key={index} onClick={() => {
                      if (t.reconciliation_status === 'CONCILIADA_TOTALMENTE' && !t.forceNew) return;
                      toggleSelection(index);
                    }}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={t.selected}
                          disabled={t.reconciliation_status === 'CONCILIADA_TOTALMENTE' && !t.forceNew}
                          onChange={() => {}} // Controlled by row click, but keep for accessibility
                          onClick={(e) => {
                            e.stopPropagation();
                            if (t.reconciliation_status === 'CONCILIADA_TOTALMENTE' && !t.forceNew) return;
                            toggleSelection(index);
                          }}
                        />
                      </td>
                      <td>{formatDate(t.date)}</td>
                      <td>
                        <div className="ofx-description-text" title={t.memo}>
                          {t.memo}
                        </div>
                        {t.name && t.name !== t.memo && (
                          <div className="ofx-name-text" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2, fontStyle: 'italic' }}>
                             OFX Name: {t.name}
                          </div>
                        )}
                        {/* Entity Identification Display */}
                        {t.relationName && (t.reconciliation_status !== 'CONCILIADA_TOTALMENTE' || t.forceNew) && (
                          <div className="ofx-entity-match" style={{ fontSize: '0.75rem', marginTop: 4, color: t.autoCreateEntity ? '#d97706' : '#4b5563', display: 'flex', alignItems: 'center' }}>
                             {t.autoCreateEntity ? (
                                 <><FiPlus size={12} style={{marginRight: 4}}/> Novo {t.relationType === 'customer' ? 'Cliente' : 'Fornecedor'}: <strong>{t.relationName}</strong></>
                             ) : (
                                 <><FiCheck size={12} style={{marginRight: 4}}/> {t.relationType === 'customer' ? 'Cliente' : 'Fornecedor'}: <strong>{t.relationName}</strong></>
                             )}
                          </div>
                        )}
                        {t.match_candidates && t.match_candidates.length > 0 && (
                          <div className="ofx-match-details">
                            {t.reconciliation_status === 'CONCILIADA_TOTALMENTE' ? (
                                <span style={{ color: 'var(--success-color, #059669)', fontWeight: 500 }}>
                                    Conciliado com: {t.match_candidates[0].description} ({formatCurrency(t.match_candidates[0].value)}) - {formatDate(t.match_candidates[0].date)}
                                </span>
                            ) : t.reconciliation_status === 'CONCILIADA_PENDENTE' ? (
                                <span style={{ color: '#0369a1', fontWeight: 500 }}>
                                    Conciliar com: {t.match_candidates[0].description} ({formatCurrency(t.match_candidates[0].value)}) - Venc: {formatDate(t.match_candidates[0].due_date || t.match_candidates[0].date)}
                                </span>
                            ) : t.reconciliation_status === 'CONCILIADA_PARCIALMENTE' ? (
                                <span style={{ color: '#c2410c', fontWeight: 500 }}>
                                    Conciliação Parcial ({t.match_candidates[0].score}%): {t.match_candidates[0].description} ({formatCurrency(t.match_candidates[0].value)})
                                </span>
                            ) : (
                                <span>
                                    Possível duplicata: {t.match_candidates[0].description} ({formatCurrency(t.match_candidates[0].value)})
                                </span>
                            )}
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                             <div style={{ flex: 1, minWidth: '150px' }}>
                                <Select
                                    placeholder="Filtrar Grupo"
                                    searchable
                                    options={[{ value: '', label: 'Todos' }, ...categories
                                        .filter(c => (t.amount >= 0 ? c.type === 'Receita' : c.type === 'Despesa') && !c.parent_id)
                                        .map(c => ({ label: c.name, value: c.id }))]}
                                    value={t.parentCategoryFilter || ''}
                                    onChange={(e) => handleQuickUpdate(index, 'parentCategoryFilter', e.target.value)}
                                    disabled={(t.reconciliation_status === 'CONCILIADA_TOTALMENTE') && !t.forceNew}
                                    icon={FiFilter}
                                />
                             </div>
                             <div style={{ flex: 1, minWidth: '150px' }}>
                                <Select
                                    placeholder="Categoria"
                                    searchable
                                    options={categories
                                        .filter(c => {
                                            const typeMatch = t.amount >= 0 ? c.type === 'Receita' : c.type === 'Despesa';
                                            if (!typeMatch) return false;
                                            if (t.parentCategoryFilter) {
                                                return c.parent_id == t.parentCategoryFilter || c.id == t.parentCategoryFilter;
                                            }
                                            return true;
                                        })
                                        .map(c => ({ label: c.name, value: c.id }))
                                    }
                                    value={t.categoryId || ''}
                                    onChange={(e) => handleQuickUpdate(index, 'categoryId', e.target.value)}
                                    disabled={(t.reconciliation_status === 'CONCILIADA_TOTALMENTE') && !t.forceNew}
                                />
                             </div>
                             <div style={{ flex: 1, minWidth: '150px' }}>
                                <Select
                                    placeholder="Centro de Custo"
                                    searchable
                                    options={costCenters.map(cc => ({ label: cc.name, value: cc.id }))}
                                    value={t.costCenterId || ''}
                                    onChange={(e) => handleQuickUpdate(index, 'costCenterId', e.target.value)}
                                    disabled={(t.reconciliation_status === 'CONCILIADA_TOTALMENTE') && !t.forceNew}
                                />
                             </div>
                        </div>
                      </td>
                      <td>
                        <span className={t.amount >= 0 ? 'ofx-amount-positive' : 'ofx-amount-negative'}>
                          {t.amount >= 0 ? 'Receita' : 'Despesa'}
                        </span>
                      </td>
                      <td className={t.amount >= 0 ? 'ofx-amount-positive' : 'ofx-amount-negative'}>
                        {formatCurrency(t.amount)}
                      </td>
                      <td>
                        {t.reconciliation_status === 'CONCILIADA_TOTALMENTE' && !t.forceNew && (
                          <span className="ofx-status-badge ofx-status-exact">
                            <FiCheck size={12} style={{ marginRight: 4 }} /> Conciliada Totalmente
                          </span>
                        )}
                        {t.reconciliation_status === 'CONCILIADA_PENDENTE' && !t.forceNew && (
                          <span className="ofx-status-badge ofx-status-match" style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}>
                            <FiCheck size={12} style={{ marginRight: 4 }} /> Pronto para Conciliar
                          </span>
                        )}
                        {t.reconciliation_status === 'CONCILIADA_PARCIALMENTE' && !t.forceNew && (
                          <span className="ofx-status-badge ofx-status-partial">
                            <FiAlertTriangle size={12} style={{ marginRight: 4 }} /> Conciliada Parcialmente
                          </span>
                        )}
                        {t.reconciliation_status === 'POSSIVEL_DUPLICIDADE' && !t.forceNew && (
                          <span className="ofx-status-badge ofx-status-match">
                            <FiAlertTriangle size={12} style={{ marginRight: 4 }} /> Possível Duplicidade
                          </span>
                        )}
                        {(t.reconciliation_status === 'NAO_CONCILIADA' || t.forceNew) && !t.configured && (
                          <Tooltip content="Necessário configurar: Tipo Parceiro, Categoria, Centro de Custo, Competência, Valor, Descrição, Condições de Pagamento">
                            <span className="ofx-status-badge ofx-status-attention">
                              <FiAlertCircle size={12} style={{ marginRight: 4 }} /> Atenção
                            </span>
                          </Tooltip>
                        )}
                        {t.configured && (
                          <span className="ofx-status-badge ofx-status-exact" style={{ marginLeft: t.reconciliation_status !== 'NAO_CONCILIADA' ? 4 : 0 }}>
                            <FiCheck size={12} style={{ marginRight: 4 }} /> Configurado
                          </span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {(t.reconciliation_status !== 'CONCILIADA_TOTALMENTE' || t.forceNew) && (
                          <button 
                            className="ofx-action-button" 
                            title="Configurar lançamento"
                            onClick={(e) => handleConfigure(index, e)}
                          >
                             <FiSettings size={16} />
                          </button>
                        )}
                        {t.reconciliation_status === 'CONCILIADA_TOTALMENTE' && !t.forceNew && (
                          <button 
                            className="ofx-action-button" 
                            title="Declarar como novo lançamento"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newTransactions = [...transactions];
                              const t = newTransactions[index];
                              
                              // Check if we can mark as configured immediately (e.g. if entity is auto-detected)
                              const hasCategory = !!t.categoryId;
                              const hasCostCenter = !!t.costCenterId; 
                              const hasRelation = !!t.relationId || !!t.autoCreateEntity;
                              const isConfigured = hasCategory && hasCostCenter && hasRelation;

                              newTransactions[index] = { ...t, forceNew: true, selected: true, configured: isConfigured };
                              setTransactions(newTransactions);
                            }}
                            style={{ width: 'auto', paddingLeft: 8, paddingRight: 8 }}
                          >
                             <FiPlus size={16} style={{ marginRight: 4 }} /> Novo
                          </button>
                        )}
                        {t.match_type === 'exact' && t.forceNew && (
                          <button 
                            className="ofx-action-button" 
                            title="Desfazer novo lançamento"
                            onClick={(e) => {
                              e.stopPropagation();
                              const newTransactions = [...transactions];
                              newTransactions[index] = { ...t, forceNew: false, selected: false };
                              setTransactions(newTransactions);
                            }}
                          >
                             <FiX size={16} />
                          </button>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {step === 'review' && (
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'space-between'}}>
            <div className="ofx-summary" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <span>{Array.isArray(transactions) ? transactions.filter(t => t.selected).length : 0} selecionados</span>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '1px solid #ccc', paddingLeft: '20px' }}>
                  {ofxBalance ? (
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                        <input 
                            type="checkbox" 
                            checked={updateBalance} 
                            onChange={(e) => setUpdateBalance(e.target.checked)} 
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                            Atualizar saldo da conta para <strong>{formatCurrency(ofxBalance.amount)}</strong>?
                        </span>
                    </label>
                  ) : (
                    <span style={{ fontSize: '0.9rem', color: '#6b7280', fontStyle: 'italic' }}>
                        Saldo não identificado no arquivo OFX
                    </span>
                  )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleClose}
                >
                    Cancelar
                </Button>
                
                {/* Apply to Selected Button - Hidden as logic is in sub-modal */}
                {Array.isArray(transactions) && transactions.some(t => t.selected) && configuringTransaction !== null && (
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {}}
                        style={{ display: 'none' }} 
                    >
                        Aplicar a Selecionados
                    </Button>
                )}

                <Button 
                    type="button" 
                    onClick={handleConfirm}
                    disabled={loading || !Array.isArray(transactions) || transactions.filter(t => t.selected).length === 0}
                    title={Array.isArray(transactions) && transactions.some(t => t.selected && (t.match_type === 'none' || t.forceNew) && !t.configured) ? "Configure os itens novos selecionados antes de importar" : ""}
                >
                    {loading ? 'Importando...' : 'Confirmar Importação'}
                    {!loading && <FiArrowRight style={{ marginLeft: 8 }} />}
                </Button>
            </div>
          </div>
        )}
      </div>

      {configuringTransaction !== null && (
        <Modal
            isOpen={true}
            onClose={() => setConfiguringTransaction(null)}
            title="Configurar Lançamento"
            size="lg"
        >
            <div style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
                <TransactionForm
                  companyId={targetCompanyId}
                  companies={companyOptions}
                  initialData={{
                    ...transactions[configuringTransaction],
                    company_id: targetCompanyId,
                    description: transactions[configuringTransaction].memo,
                    value: Math.abs(transactions[configuringTransaction].amount),
                    type: transactions[configuringTransaction].amount >= 0 ? 'revenue' : 'expense',
                    competenceDate: transactions[configuringTransaction].date,
                    date: transactions[configuringTransaction].date,
                    due_date: transactions[configuringTransaction].date,
                    accountId: account.id,
                    status: 'paid',
                    paymentDate: transactions[configuringTransaction].date,
                    isPaid: true,
                    paymentMethod: transactions[configuringTransaction].paymentMethod,
                    
                    category_id: transactions[configuringTransaction].categoryId,
                    cost_center_id: transactions[configuringTransaction].costCenterId,

                    autoCreateEntity: transactions[configuringTransaction].autoCreateEntity,
                    relationName: transactions[configuringTransaction].relationName,
                    relationType: transactions[configuringTransaction].relationType,
                    customer_id: transactions[configuringTransaction].relationType === 'customer' && !transactions[configuringTransaction].autoCreateEntity ? transactions[configuringTransaction].relationId : null,
                    supplier_id: transactions[configuringTransaction].relationType === 'supplier' && !transactions[configuringTransaction].autoCreateEntity ? transactions[configuringTransaction].relationId : null,
                  }}
                  onSubmit={handleSaveConfiguration}
                  onApplyToSelected={handleApplyToSelected}
                  onCancel={() => setConfiguringTransaction(null)}
                  onSuccess={() => {}}
                />
            </div>
        </Modal>
      )}
    </Modal>
  );
}
